import type { AnchorHTMLAttributes } from 'react';
import { useLinkClickHandler, useLocation } from 'react-router-dom';

type FragmentLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { hash: string };

// A router entry gives every jump its own scroll position. Preserve the query
// string too: an in-page link must not discard the reader's current URL state.
export default function FragmentLink({ hash, onClick, ...props }: FragmentLinkProps) {
  const { pathname, search } = useLocation();
  const navigate = useLinkClickHandler<HTMLAnchorElement>({ pathname, search, hash }, { target: props.target });
  // Keep the native fragment href for prerendered/no-script pages and modified
  // clicks. The public router handler provides Link's history behavior after
  // hydration, without query-dependent attributes that differ from static HTML.
  return <a {...props} href={hash} onClick={(event) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (props.target && props.target !== '_self')) return;
    let id;
    try { id = decodeURIComponent(hash.replace(/^#/, '')); } catch { return; }
    const target = document.getElementById(id);
    if (target) {
      // Match native fragment-link keyboard behavior without starting a second
      // scroll. ScrollRestoration owns the actual position after navigation.
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
    navigate(event);
  }} />;
}
